import * as React from 'react';
import {ReactSVG} from "react-svg";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import {DataNode, Element} from "domhandler/lib/node";
import parseToReact, {domToReact} from "html-react-parser";
import {differenceWith, has} from "lodash";
import * as uuidv4 from "uuid/v4";
import AnchorButton from "../../components/AnchorButton";
import Link from "../../components/Link";
import {Entry} from "../../contract/notion";
import {isEqualsArray} from "../../util/helper";
import hstatic, {staticHypotheekbondUrl} from "../../util/hstatic";
import {ContentApiDef} from "../..";

type Link = [string, string];

type ParserOptionsDef = (
  self: ContentApiDef,
  links: Link[]
) => {
  [key: string]: any;
}

export const parserOptions: ParserOptionsDef = (
  self,
  links
) => {
  const {docStack} = self.options.get('entry.adapter');

  const options = {
    replace: (domNode: Element): React.ReactElement|Element => {
      if (domNode instanceof Element && (domNode.name === 'ul' || domNode.name === 'fieldset')) {
        const {childNodes} = domNode;

        if ((childNodes || []).find((node: DataNode) => (
          ((node instanceof Element && node.name === 'li') && has(node.attribs, 'aria-expanded')) ||
          (node instanceof Element && node.name === 'label')
        ))) {
          const uuids = childNodes.map(v => uuidv4()); // eslint-disable-line @typescript-eslint/no-unused-vars

          // state
          let isChecked, isToggle;
          let isChecklist = childNodes.filter(node => (node instanceof Element && node.name === 'label')).length > 1;
          let isRecommendation = childNodes.filter(node => (node instanceof Element && node.name === 'label')).length === 1;

          const preExpanded = (
            isChecklist ||
            isRecommendation ||
            (childNodes || []).find((node: DataNode) => (node instanceof Element && node.name === 'li'))
          ) ? [uuids[0]]
            : [];

          return (
            <Accordion
              allowMultipleExpanded={true}
              allowZeroExpanded={true}
              preExpanded={preExpanded}
            >
              {childNodes.map((child: any, index: number) => {
                if (!child || child.name === 'input') return null;

                const [heading, ...rest] = (child.childNodes || []) as any;
                const title: string = heading?.nodeValue
                  ? heading?.nodeValue
                  : heading?.childNodes[0]?.nodeValue;

                isToggle = child.name === 'li';
                isChecklist = child.name !== 'li' && isChecklist;
                isRecommendation = child.name !== 'li' && isRecommendation;
                isChecked = child.name !== 'li' && child.nextSibling instanceof Element && child.nextSibling.name === 'input' && has(child.nextSibling.attribs, 'checked');
                return (
                  <AccordionItem
                    uuid={uuids[index]}
                    key={uuids[index]}
                    className={
                      `accordion__item accordion__item--has-icon${
                        isRecommendation
                          ? ' theme--bg-card-color theme--thumbnail-border-radius form-row-padding gutter--1-2-y'
                          : ''
                      }`
                    }
                  >
                    <AccordionItemHeading className={"theme--primary-color"}>
                      <AccordionItemButton>
                        {isToggle && (<i className={"accordion__arrow theme--highlight-color"} />)}
                        {isChecklist && (
                          isChecked ? (
                            <ReactSVG
                              className={"theme--highlight-color"}
                              src={hstatic(`${staticHypotheekbondUrl()}images/icon/check.svg`)}
                            />
                          ) : (<i style={{width: '30px'}}>—</i>)
                        )}
                        {isRecommendation && (
                          <ReactSVG
                            className={"theme--highlight-color"}
                            src={hstatic(`${staticHypotheekbondUrl()}images/icon/thumb.svg`)}
                          />
                        )}
                        <strong className={"fw-600"}>{title}</strong>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>{domToReact(rest, options)}</AccordionItemPanel>
                  </AccordionItem>
                )
              }) || null}
            </Accordion>
          )
        }
      } else if (domNode instanceof Element && domNode.name === 'h2') {
        const {attribs} = domNode;
        attribs.className = `${attribs.className ? attribs.className + ' ': ''}f-3 theme--primary-color fw-600`
      } else if (domNode instanceof Element && domNode.name === 'h3') {
        const {attribs} = domNode;
        attribs.className = `${attribs.className ? attribs.className + ' ': ''}f-5 theme--primary-color fw-600`
      } else if (domNode instanceof Element && domNode.name === 'blockquote') {
        const {attribs, childNodes} = domNode;
        attribs.className = `${attribs.className ? attribs.className + ' ': ''}f-2 fw-600`

        const isQuote = childNodes.find((node: any) => node && node.name === 'cite');
        if (isQuote) {
          attribs.className = `${attribs.className} theme--primary-color is-quote`;
        } else {
          attribs.className = `${attribs.className} theme--highlight-color`;
        }

        return (
          <blockquote {...attribs}>
            {isQuote && (
              <ReactSVG
                className={"theme--highlight-color"}
                src={hstatic(`${staticHypotheekbondUrl()}images/icon/quote.svg`)}
              />
            )}

            {domToReact(childNodes, options)}
          </blockquote>
        )
      } else if (domNode instanceof Element && domNode.name === 'cite') {
        const {attribs} = domNode;
        attribs.className = `${attribs.className ? attribs.className + ' ': ''}f-6 fw-600 theme--secondary-color`
      } else if (domNode instanceof Element && domNode.name === 'aside') {
        const {children, attribs} = domNode;
        attribs.className = `${attribs.className ? attribs.className + ' ': ''}f-5 theme--primary-color theme--bg-card-color theme--thumbnail-border-radius`

        let hasHeading = false;
        return (
          <aside {...attribs}>
            <section>
              {Array.from(children).map((child: any, index: number) => {

                if (!index) {
                  if (child && child.name === 'strong') {
                    hasHeading = true;
                    const {attribs} = child;
                    attribs.className = `${attribs.className ? attribs.className + ' ': ''}f-3 theme--primary-color fw-600`;
                    const [heading, ...rest] = (child.childNodes || []) as any; // eslint-disable-line @typescript-eslint/no-unused-vars
                    const title: string = heading?.nodeValue as string;

                    return <h2 key={`aside-${index}`} {...attribs}>{title}</h2>
                  }
                }
              })}

              {domToReact(domNode.childNodes.slice(hasHeading ? 1 : 0), options)}
            </section>
          </aside>
        )
      } else if (domNode instanceof Element && domNode.name === 'a') {
        const {childNodes, attribs} = domNode;
        attribs.className = `${attribs.className ? attribs.className + ' ': ''}theme--highlight-color fw-600`

        if (attribs.href) {
          // try to resolve by uuid
          const match: Entry = self.articles.find(({id, slug}) => {
            if (!id) return false; // skip skeleton entries

            const found: boolean = (id.replace(/-/g, '') === attribs.href.slice(1));

            // side-effect (cf. parseContent below)
            if (found) links.push([`/${slug}`, id]); // e.g. ['/app/slug-of-route', '870bbc94-f8ed-4ca2-be32-b42d5517b37c']
            return found;
          });

          // internal anchor
          if (match) {
            const {slug: href, ...rest} = match; // eslint-disable-line @typescript-eslint/no-unused-vars
            const child: DataNode = childNodes[0] as DataNode;
            const title: string = child?.nodeValue;

            return (
              <AnchorButton
                style={{fontSize: 'inherit'}}
                onClick={(): void => {
                  docStack.push(`/${href}`);
                }}
                {...attribs}
              >
                {title}
              </AnchorButton>
            );
          } else {
            const child: DataNode = childNodes[0] as DataNode;
            const title: string = child?.nodeValue;
            attribs.className = `${attribs.className ? attribs.className + ' ': ''}is-external`

            return (
              <Link {...attribs} to={attribs.href}>
                <span>
                  {title}
                  <ReactSVG src={hstatic(`${staticHypotheekbondUrl()}images/icon/external.svg`)} />
                </span>
              </Link>
            );
          }
        }
      }

      return domNode;
    }
  }

  return options;
};

// parse HTML string to React nodes
type ParseContentDef = (
  self: ContentApiDef,
  str: string,
) => {
  content: React.ReactElement;
  links: Link[]
}

export const parseContent: ParseContentDef = (
  self,
  str
) => {
  const options = parserOptions.bind(self, self);
  const links = [];

  let content = (parseToReact(str, options(links)) || []);
  if (!Array.isArray(content)) content = [];

  // make sure each element has a key, and we end up with a single top-level node
  content = content.map(n => React.cloneElement(n, {key: uuidv4()}));
  content = (<>{content.map(n => n)}</>);

  return {
    content,
    links
  };
}

// look up linked docs and pre-fetch their contents
type PrefetchContentDef = (
  self: ContentApiDef,
  links: Link[]
) => void;

export const prefetchContent: PrefetchContentDef = (
  self,
  links
) => {
  const {docs} = self.options.get('entry.adapter');

  const diff: any[] = differenceWith(
    links,
    (docs || []).map(({uuid, path}: { uuid: string; path: string }): Link => [path, uuid]),
    isEqualsArray
  );
  if (!diff.length) return;

  diff.map(([slug, id]: Link) => self.queue.add(() => self.getArticleContent(slug), {id}));
}

export type AdaptNotionDef = (
  self: ContentApiDef,
  parse: (str: string) => {content: React.ReactElement; links: Link[]},
  prefetch: (links: Link[]) => void,
  entry: Entry,
) => Entry;

const adaptNotion: AdaptNotionDef = (
  self, // could not assign 'this' :(
  parse,
  prefetch,
  entry
): Entry => {
  if (!parse || !prefetch) return;

  const {content, links} = parse(entry.content);
  entry.rendered = content;
  entry.links = links;

  // @todo make this configurable (e.g. [n] levels recursion)
  prefetch(links);

  return entry;
};

export default (
  self: ContentApiDef
) => (
  entry: Entry
): Entry => adaptNotion(
  self,
  parseContent.bind(null, self),
  prefetchContent.bind(null, self),
  entry
);
